'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { AuthError, Session, User } from '@supabase/supabase-js';
import type { AppRole } from '../types/roles';
import { ROLE_CONFIGS } from '../types/roles';

// ── Types ────────────────────────────────────────────────────────────────────

export interface UserProfile {
  id: string;
  email: string;
  full_name?: string;
  role: AppRole;
  avatar_url?: string;
  phone?: string;
  is_active?: boolean;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  userRole: AppRole | null;
  loading: boolean;
  profileLoading: boolean;
  sessionExpired: boolean;
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<{ error: any }>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ data: any; error: any }>;
  refreshSession: () => Promise<boolean>;
  isAuthenticated: boolean;
  hasRole: (role: AppRole) => boolean;
  getRedirectRoute: () => string;
  getLoginRoute: () => string;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

// ── Auth error logger ─────────────────────────────────────────────────────────
function logAuthError(context: string, error: unknown) {
  const msg = error instanceof Error ? error.message : String(error);
  console.error(`[AuthContext][${context}]`, msg, error);
}

/**
 * Extract the best available role from a Supabase User object.
 * Priority: app_metadata.role > user_metadata.role > null
 * NEVER defaults to 'customer' — returns null if role is unknown.
 */
function extractRoleFromUser(user: User): AppRole | null {
  const appRole = user.app_metadata?.role as AppRole | undefined;
  const metaRole = user.user_metadata?.role as AppRole | undefined;
  const validRoles: AppRole[] = ['admin', 'vendor', 'driver', 'customer'];
  if (appRole && validRoles.includes(appRole)) return appRole;
  if (metaRole && validRoles.includes(metaRole)) return metaRole;
  return null;
}

// ── Provider ─────────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Cache: prevent repeated DB queries for the same user
  const profileCacheRef = useRef<{ userId: string; profile: UserProfile } | null>(null);
  // Track in-flight profile fetch to prevent concurrent duplicate requests
  const profileFetchInProgressRef = useRef<string | null>(null);

  // ── Load profile with caching and deduplication ───────────────────────────
  const loadProfile = useCallback(async (userId: string, userEmail?: string, userObj?: User, retryCount = 0) => {
    if (!userId) return;

    // Return cached profile immediately if available for this user
    if (profileCacheRef.current?.userId === userId) {
      console.info(`[AuthContext] Using cached profile for user ${userId}, role=${profileCacheRef.current.profile.role}`);
      setUserProfile(profileCacheRef.current.profile);
      return;
    }

    // Prevent concurrent fetches for the same user
    if (profileFetchInProgressRef.current === userId) {
      console.info(`[AuthContext] Profile fetch already in progress for user ${userId}, skipping duplicate`);
      return;
    }

    profileFetchInProgressRef.current = userId;
    setProfileLoading(true);

    // Determine the best known role from auth metadata BEFORE any DB query.
    // This is the "trusted" role — we never downgrade it to 'customer' on DB failure.
    const trustedRole: AppRole | null = userObj ? extractRoleFromUser(userObj) : null;

    try {
      // Primary: profiles table
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (!error && profile) {
        // Use DB role if present; fall back to trusted auth metadata role; never assume customer
        const dbRole = profile.role as AppRole | undefined;
        const resolvedRole: AppRole = dbRole || trustedRole || 'customer';
        const resolvedProfile: UserProfile = {
          id: profile.id,
          email: profile.email || userEmail || '',
          full_name: profile.full_name,
          role: resolvedRole,
          avatar_url: profile.avatar_url,
          phone: profile.phone,
          is_active: profile.is_active ?? true,
        };
        console.info(`[AuthContext] Profile loaded from profiles table: role=${resolvedProfile.role}`);
        profileCacheRef.current = { userId, profile: resolvedProfile };
        setUserProfile(resolvedProfile);
        return;
      }

      if (error) {
        // code 42501 = permission denied — RLS policy issue, skip noisy error log and fall through
        if (error.code !== '42501' && error.code !== 'PGRST301' && error.code !== 'PGRST116') {
          logAuthError('loadProfile/profiles', error);
        } else {
          console.warn(`[AuthContext] profiles RLS denied (code=${error.code}), falling back to auth metadata`);
        }
      }

      // Fallback: user_roles table
      const { data: roleRow, error: roleError } = await supabase
        .from('user_roles')
        .select('role, email')
        .eq('user_id', userId)
        .single();

      if (!roleError && roleRow) {
        // Use DB role if present; fall back to trusted auth metadata role; never assume customer
        const dbRole = roleRow.role as AppRole | undefined;
        const resolvedRole: AppRole = dbRole || trustedRole || 'customer';
        const resolvedProfile: UserProfile = {
          id: userId,
          email: roleRow.email || userEmail || '',
          role: resolvedRole,
        };
        console.info(`[AuthContext] Profile loaded from user_roles table: role=${resolvedProfile.role}`);
        profileCacheRef.current = { userId, profile: resolvedProfile };
        setUserProfile(resolvedProfile);
        return;
      }

      // Last resort: auth metadata (no DB query needed)
      const { data: authData } = await supabase.auth.getUser();
      if (authData?.user) {
        // CRITICAL: use extractRoleFromUser — never default to 'customer' here
        const metaRole = extractRoleFromUser(authData.user);
        const resolvedProfile: UserProfile = {
          id: authData.user.id,
          email: authData.user.email || '',
          // Only fall back to 'customer' if we truly have no role signal at all
          role: metaRole || trustedRole || 'customer',
        };
        console.info(`[AuthContext] Profile loaded from auth metadata: role=${resolvedProfile.role}`);
        profileCacheRef.current = { userId, profile: resolvedProfile };
        setUserProfile(resolvedProfile);
        return;
      }

      // Retry up to 2 times with exponential backoff (handles race conditions on new signup)
      if (retryCount < 2) {
        const delay = Math.pow(2, retryCount) * 500;
        console.info(`[AuthContext] Profile not found, retrying in ${delay}ms (attempt ${retryCount + 1})`);
        profileFetchInProgressRef.current = null; // Allow retry
        await new Promise((r) => setTimeout(r, delay));
        return loadProfile(userId, userEmail, userObj, retryCount + 1);
      }

      // After all retries: use trusted role from auth metadata — NEVER blindly default to 'customer'
      const fallbackRole: AppRole = trustedRole || 'customer';
      console.warn(
        `[AuthContext] Could not load profile after ${retryCount} retries — using auth metadata role: ${fallbackRole}`,
      );
      const fallbackProfile: UserProfile = {
        id: userId,
        email: userEmail || '',
        role: fallbackRole,
      };
      setUserProfile(fallbackProfile);

    } catch (err) {
      logAuthError('loadProfile', err);
      // Retry on network errors
      if (retryCount < 2) {
        const delay = Math.pow(2, retryCount) * 500;
        profileFetchInProgressRef.current = null; // Allow retry
        await new Promise((r) => setTimeout(r, delay));
        return loadProfile(userId, userEmail, userObj, retryCount + 1);
      }
      // Final fallback — preserve trusted role, never downgrade to 'customer' on network error
      const fallbackRole: AppRole = trustedRole || 'customer';
      console.warn(`[AuthContext] Network error loading profile — preserving role: ${fallbackRole}`);
      const fallbackProfile: UserProfile = {
        id: userId,
        email: userEmail || '',
        role: fallbackRole,
      };
      setUserProfile(fallbackProfile);
    } finally {
      profileFetchInProgressRef.current = null;
      setProfileLoading(false);
    }
  }, []);

  const clearProfile = useCallback(() => {
    profileCacheRef.current = null;
    profileFetchInProgressRef.current = null;
    setUserProfile(null);
    setProfileLoading(false);
    console.info('[AuthContext] Profile cleared');
  }, []);

  // ── Session refresh ───────────────────────────────────────────────────────
  const refreshSession = useCallback(async (): Promise<boolean> => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        logAuthError('refreshSession', error);
        return false;
      }
      if (data?.session?.user) {
        setUser(data.session.user);
        setSessionExpired(false);
        // Invalidate cache on token refresh so role is re-read
        profileCacheRef.current = null;
        await loadProfile(data.session.user.id, data.session.user.email, data.session.user);
        return true;
      }
      return false;
    } catch (err) {
      logAuthError('refreshSession', err);
      return false;
    }
  }, [loadProfile]);

  // ── Auth state listener ───────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        logAuthError('getSession', error);
      }
      if (!mounted) return;

      console.info(`[AuthContext] Initial session check: hasSession=${!!session}, userId=${session?.user?.id || 'none'}`);
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        loadProfile(session.user.id, session.user.email, session.user);
      } else {
        clearProfile();
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session: Session | null) => {
        if (!mounted) return;

        console.info(`[AuthContext] Auth event: ${event}, hasSession=${!!session}, userId=${session?.user?.id || 'none'}`);

        if (event === 'TOKEN_REFRESHED') {
          console.info('[AuthContext] Session token refreshed successfully');
          setSessionExpired(false);
          // Invalidate profile cache on token refresh so role is re-read
          if (session?.user && profileCacheRef.current?.userId === session.user.id) {
            // Keep cache valid — role doesn't change on token refresh
            console.info('[AuthContext] Keeping profile cache after token refresh');
          }
        }

        if (event === 'SIGNED_OUT') {
          setUser(null);
          clearProfile();
          setLoading(false);
          setSessionExpired(false);
          return;
        }

        // Handle session expiry — attempt refresh before treating as signed out
        if (!session && event !== 'SIGNED_OUT') {
          console.warn('[AuthContext] Session lost — attempting refresh');
          const refreshed = await refreshSession();
          if (!refreshed) {
            console.warn('[AuthContext] Session refresh failed — marking session as expired');
            setSessionExpired(true);
            setUser(null);
            clearProfile();
            setLoading(false);
          }
          return;
        }

        setUser(session?.user ?? null);
        setLoading(false);

        if (session?.user) {
          setSessionExpired(false);
          // Only reload profile if it's a different user or cache is empty
          if (profileCacheRef.current?.userId !== session.user.id) {
            await loadProfile(session.user.id, session.user.email, session.user);
          } else {
            console.info('[AuthContext] Profile cache hit on auth state change — skipping DB query');
            setUserProfile(profileCacheRef.current.profile);
          }
        } else {
          clearProfile();
        }
      },
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [loadProfile, clearProfile, refreshSession]);

  // ── Auto session refresh before expiry ───────────────────────────────────
  useEffect(() => {
    if (!user) return;

    const checkAndRefresh = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const expiresAt = session.expires_at ?? 0;
      const nowSecs = Math.floor(Date.now() / 1000);
      const secsUntilExpiry = expiresAt - nowSecs;

      // Refresh if less than 5 minutes remaining
      if (secsUntilExpiry < 300) {
        console.info('[AuthContext] Session expiring soon, refreshing...');
        await refreshSession();
      }
    };

    // Check every 4 minutes
    const interval = setInterval(checkAndRefresh, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, refreshSession]);

  // ── signIn ────────────────────────────────────────────────────────────────
  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        logAuthError('signIn', error);
        const msg = normalizeAuthError(error);
        return { data: null, error: { ...error, message: msg } };
      }

      if (data?.user) {
        setSessionExpired(false);
        // Sync role across all tables
        try {
          await supabase.rpc('ensure_admin_role_on_signin', {
            signin_user_id: data.user.id,
            signin_email: email,
          });
        } catch (rpcErr) {
          console.warn('[AuthContext] Role sync RPC error (non-fatal):', rpcErr);
        }
        // Invalidate cache so fresh profile is loaded after sign-in
        profileCacheRef.current = null;
        await loadProfile(data.user.id, email, data.user);
      }

      return { data, error: null };
    } catch (err) {
      logAuthError('signIn/network', err);
      return { data: null, error: { message: 'Network error. Please check your connection and try again.' } };
    }
  };

  // ── signUp ────────────────────────────────────────────────────────────────
  const signUp = async (
    email: string,
    password: string,
    metadata: Record<string, any> = {},
  ) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata },
      });
      if (error) {
        logAuthError('signUp', error);
        return { data: null, error: { ...error, message: normalizeAuthError(error) } };
      }
      return { data, error: null };
    } catch (err) {
      logAuthError('signUp/network', err);
      return { data: null, error: { message: 'Network error. Please check your connection and try again.' } };
    }
  };

  // ── signOut ───────────────────────────────────────────────────────────────
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        logAuthError('signOut', error);
      }
      setUser(null);
      clearProfile();
      setSessionExpired(false);
      console.info('[AuthContext] User signed out');
      return { error };
    } catch (err) {
      logAuthError('signOut/network', err);
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  // ── updateProfile ─────────────────────────────────────────────────────────
  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return { data: null, error: { message: 'No user logged in' } };
    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();
      if (error) {
        logAuthError('updateProfile', error);
        return { data: null, error };
      }
      if (data) {
        const updated = data as UserProfile;
        profileCacheRef.current = { userId: user.id, profile: updated };
        setUserProfile(updated);
      }
      return { data, error: null };
    } catch (err) {
      logAuthError('updateProfile/network', err);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const userRole: AppRole | null =
    (userProfile?.role as AppRole) ||
    (user ? extractRoleFromUser(user) : null) ||
    null;

  const hasRole = (role: AppRole) => userRole === role;

  /**
   * Returns the correct post-login redirect route based on role.
   * admin  → /apps/admin
   * vendor → /apps/vendor
   * driver → /driver-home
   * customer → /customer-home
   */
  const getRedirectRoute = (): string => {
    if (!userRole) return '/';
    return ROLE_CONFIGS[userRole]?.defaultRoute || '/';
  };

  /**
   * Returns the correct login page for the current role.
   */
  const getLoginRoute = (): string => {
    if (!userRole) return '/apps/admin/login';
    return ROLE_CONFIGS[userRole]?.loginRoute || '/apps/admin/login';
  };

  const value: AuthContextType = {
    user,
    userProfile,
    userRole,
    loading,
    profileLoading,
    sessionExpired,
    signIn,
    signUp,
    signOut,
    updateProfile,
    refreshSession,
    isAuthenticated: !!user,
    hasRole,
    getRedirectRoute,
    getLoginRoute,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ── Error message normalizer ──────────────────────────────────────────────────
function normalizeAuthError(error: AuthError | { message?: string }): string {
  const msg = (error as AuthError).message || '';
  const code = (error as any).code || '';

  if (
    msg.toLowerCase().includes('invalid login credentials') ||
    msg.toLowerCase().includes('invalid credentials') ||
    code === 'invalid_credentials'
  ) {
    return 'Invalid email or password. Please check your credentials and try again.';
  }
  if (msg.toLowerCase().includes('email not confirmed')) {
    return 'Please confirm your email address before signing in. Check your inbox for a confirmation link.';
  }
  if (msg.toLowerCase().includes('user not found')) {
    return 'No account found with this email address.';
  }
  if (msg.toLowerCase().includes('too many requests') || code === 'over_request_rate_limit') {
    return 'Too many login attempts. Please wait a few minutes and try again.';
  }
  if (msg.toLowerCase().includes('network') || msg.toLowerCase().includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }
  return msg || 'An unexpected error occurred. Please try again.';
}

export default AuthProvider;
